import {
  Badge,
  Box,
  Button,
  Flex,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { useEffect } from "react"
import { HtmlContent } from "../Common/HtmlContent"

import { type QuestionPublic, QuestionsService } from "@/client"
import type { ApiError } from "@/client/core/ApiError"
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from "@/components/ui/dialog"
import { handleError } from "@/utils"

interface QuestionPreviewModalProps {
  questionId: string | null
  isOpen: boolean
  onClose: () => void
}

export function QuestionPreviewModal({
  questionId,
  isOpen,
  onClose,
}: QuestionPreviewModalProps) {
  const {
    data: question,
    isLoading,
    isError,
    error,
  } = useQuery<QuestionPublic, ApiError>({
    queryKey: ["question-preview", questionId],
    queryFn: () => QuestionsService.getQuestion({ questionId: questionId! }),
    enabled: isOpen && Boolean(questionId),
  })

  useEffect(() => {
    if (isError && error) {
      handleError(error)
    }
  }, [error, isError])

  return (
    <DialogRoot
      open={isOpen}
      onOpenChange={({ open }) => {
        if (!open) {
          onClose()
        }
      }}
    >
      <DialogContent maxW="2xl">
        <DialogHeader>
          <DialogTitle>Question Preview</DialogTitle>
        </DialogHeader>
        <DialogCloseTrigger />
        <DialogBody>
          {isLoading ? (
            <VStack align="center" py={8}>
              <Spinner />
            </VStack>
          ) : question ? (
            <VStack align="stretch" gap={4}>
              <Box
                p={4}
                borderWidth="1px"
                borderRadius="md"
                bg="blue.50"
                _dark={{ bg: "blue.900" }}
              >
                <HtmlContent content={question.question_text} />
              </Box>
              <VStack align="stretch">
                <Text fontWeight="semibold">Choices</Text>
                <Box
                  as="ul"
                  pl={4}
                  display="flex"
                  flexDirection="column"
                  gap={2}
                >
                  {question.options.map((option, idx) => (
                    <Box
                      as="li"
                      key={idx}
                      p={3}
                      borderWidth="1px"
                      borderRadius="md"
                      bg={
                        idx === question.correct_answer ? "green.50" : "white"
                      }
                      _dark={{
                        bg:
                          idx === question.correct_answer
                            ? "green.900"
                            : "gray.900",
                      }}
                    >
                      <HtmlContent
                        as="span"
                        style={{
                          fontWeight:
                            idx === question.correct_answer ? "bold" : "normal",
                        }}
                        content={option}
                      />
                    </Box>
                  ))}
                </Box>
              </VStack>
              {question.rationale && (
                <VStack align="stretch" gap={2}>
                  <Text fontWeight="semibold">Rationale</Text>
                  <Box
                    p={4}
                    borderWidth="1px"
                    borderRadius="md"
                    bg="purple.50"
                    _dark={{ bg: "purple.900" }}
                  >
                    <HtmlContent content={question.rationale} />
                  </Box>
                </VStack>
              )}
              <Box
                borderWidth="1px"
                borderRadius="md"
                p={4}
                bg="gray.50"
                _dark={{ bg: "gray.900" }}
              >
                <Flex
                  direction={{ base: "column", md: "row" }}
                  gap={{ base: 4, md: 8 }}
                  align="flex-start"
                >
                  <VStack align="flex-start" gap={2} flex="1">
                    <Text fontWeight="semibold">Subjects</Text>
                    <Flex gap={2} wrap="wrap">
                      {question.subjects.length > 0 ? (
                        question.subjects.map((subject) => (
                          <Badge key={subject.id} colorPalette="blue">
                            {subject.name}
                          </Badge>
                        ))
                      ) : (
                        <Text color="fg.muted">No subjects</Text>
                      )}
                    </Flex>
                  </VStack>
                  <VStack align="flex-start" gap={2} flex="1">
                    <Text fontWeight="semibold">Topics</Text>
                    <Flex gap={2} wrap="wrap">
                      {question.topics.length > 0 ? (
                        question.topics.map((topic) => (
                          <Badge key={topic.id} colorPalette="green">
                            {topic.name}
                          </Badge>
                        ))
                      ) : (
                        <Text color="fg.muted">No topics</Text>
                      )}
                    </Flex>
                  </VStack>
                </Flex>
              </Box>
            </VStack>
          ) : (
            <Text color="gray.500">Question data unavailable.</Text>
          )}
        </DialogBody>
        <DialogFooter>
          <Button onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  )
}
